import type { SongRequest } from '../services/requests';
import type { PlayMode } from '../services/playModes';

export const MAX_QUEUE_DEPTH = 10;

export type QueueNextLockDecision = 'lock' | 'cap-full' | 'already-locked' | 'off';

export interface QueuePositionRow {
  request: SongRequest;
  position: number;
}

export interface TopQueueStatus {
  depth: number;
  queuedCount: number;
  unqueued: Map<string, number>;
}

export function isQueuedInPlayer(request: Pick<SongRequest, 'queuedInPlayer' | 'queuedInSpotify'>): boolean {
  return Boolean(request.queuedInPlayer ?? request.queuedInSpotify);
}

// A lock counts as created by Queue Next only when both fields agree (ITER-171)
export function isQueueLock(request: Pick<SongRequest, 'lockedNext' | 'queueLockedAt'>): boolean {
  return request.lockedNext === true && typeof request.queueLockedAt === 'string';
}

export function autoLockCap(depth: number): number {
  return Math.max(depth, 1);
}

// forceLock: a song flagged "Bridge didn't recognize" marked queued as the next song locks
// whatever the toggle and cap say (ITER-172)
export function decideQueueNextLock({
  request,
  lockWhenQueued,
  depth,
  queue,
  forceLock = false,
}: {
  request: Pick<SongRequest, 'lockedNext'>;
  lockWhenQueued: boolean;
  depth: number;
  queue: Pick<SongRequest, 'lockedNext' | 'queueLockedAt'>[];
  forceLock?: boolean;
}): QueueNextLockDecision {
  if (request.lockedNext) return lockWhenQueued || forceLock ? 'already-locked' : 'off';
  if (forceLock) return 'lock';
  if (!lockWhenQueued) return 'off';
  if (queue.filter(isQueueLock).length >= autoLockCap(depth)) return 'cap-full';
  return 'lock';
}

export function buildQueueNextUpdate(decision: QueueNextLockDecision, nowIso: string): Partial<SongRequest> {
  const queued: Partial<SongRequest> = { queuedInPlayer: true, queuedInSpotify: true };
  return decision === 'lock' ? { ...queued, lockedNext: true, queueLockedAt: nowIso } : queued;
}

export function buildUnqueueUpdate(request: Pick<SongRequest, 'lockedNext' | 'queueLockedAt'>): Partial<SongRequest> {
  const unqueued: Partial<SongRequest> = { queuedInPlayer: false, queuedInSpotify: false };
  return isQueueLock(request) ? { ...unqueued, lockedNext: false, queueLockedAt: undefined } : unqueued;
}

export function buildManualLockToggleUpdate(request: Pick<SongRequest, 'lockedNext'>): Partial<SongRequest> {
  return { lockedNext: !request.lockedNext, queueLockedAt: undefined };
}

// Call only for two locked songs. Other locks (manual, local file, prediction) stay ahead of queue locks,
// and queue locks keep the order they were queued in, so the order stays consistent
export function compareQueueLocks(
  a: Pick<SongRequest, 'lockedNext' | 'queueLockedAt'>,
  b: Pick<SongRequest, 'lockedNext' | 'queueLockedAt'>,
): number {
  const aQueueLock = isQueueLock(a);
  const bQueueLock = isQueueLock(b);
  if (aQueueLock !== bQueueLock) return aQueueLock ? 1 : -1;
  if (!aQueueLock) return 0;
  return (a.queueLockedAt as string).localeCompare(b.queueLockedAt as string);
}

export function playerSongsInOrder(displayQueue: SongRequest[]): SongRequest[] {
  return displayQueue.filter(request => !request.localFile);
}

export function topQueueStatus(displayQueue: SongRequest[], depth: number): TopQueueStatus | null {
  if (depth === 0) return null;
  const playerSongs = playerSongsInOrder(displayQueue);
  if (playerSongs.length === 0) return null;
  const top = playerSongs.slice(0, depth);
  const unqueued = new Map<string, number>();
  for (const request of top) {
    if (!isQueuedInPlayer(request)) unqueued.set(request.id, displayQueue.indexOf(request) + 1);
  }
  return { depth: top.length, queuedCount: top.length - unqueued.size, unqueued };
}

export function nextPlayerSong(displayQueue: SongRequest[]): SongRequest | null {
  return playerSongsInOrder(displayQueue)[0] ?? null;
}

export function allQueuedRows(displayQueue: SongRequest[]): QueuePositionRow[] {
  return displayQueue
    .map((request, index) => ({ request, position: index + 1 }))
    .filter(row => isQueuedInPlayer(row.request));
}

// Songs past the depth, lowest in the queue first (ITER-169)
export function cleanQueueRows(displayQueue: SongRequest[], depth: number): QueuePositionRow[] {
  return allQueuedRows(displayQueue).slice(depth).reverse();
}

export function clampQueueDepth(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.min(MAX_QUEUE_DEPTH, Math.max(0, Math.round(value)));
}

// Legacy events with no mode are Spotify manual
export function isManualPlayMode(mode: PlayMode | undefined): boolean {
  return !mode || mode.endsWith('_manual');
}
